/**
 * Core session management for CDP SDK (frontend version).
 *
 * Uses localStorage for persistence, axios for HTTP, and jsencrypt for RSA.
 */

import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { JSEncrypt } from 'jsencrypt';
import type { CaptchaInfo, SessionData, RequestConfig } from './types';

const STORAGE_KEY = 'cli-anything-cdp-session';

/** Convert base64 DER public key to PEM format for jsencrypt. */
function derToPem(base64Der: string): string {
  const header = '-----BEGIN PUBLIC KEY-----';
  const footer = '-----END PUBLIC KEY-----';
  const chunked = base64Der.match(/.{1,64}/g)?.join('\n') ?? base64Der;
  return `${header}\n${chunked}\n${footer}`;
}

export class CDPSession {
  baseUrl: string | null = null;
  token: string | null = null;
  tenantId: number | null = null;
  userId: number | null = null;
  username: string | null = null;
  md5key: string | null = null;
  publicKey: string | null = null;
  currentProjectUuid: string | null = null;

  private axiosInstance: AxiosInstance;

  constructor(private readonly persistent = true) {
    this.axiosInstance = axios.create();
    this._loadSession();
  }

  private _loadSession() {
    if (!this.persistent) return;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data: SessionData = JSON.parse(raw);
        this.baseUrl = data.baseUrl ?? null;
        this.token = data.token ?? null;
        this.tenantId = data.tenantId ?? null;
        this.userId = data.userId ?? null;
        this.username = data.username ?? null;
        this.md5key = data.md5key ?? null;
        this.publicKey = data.publicKey ?? null;
        this.currentProjectUuid = data.currentProjectUuid ?? null;
      }
    } catch {
      // ignore parse errors
    }
  }

  saveSession() {
    if (!this.persistent) return;
    const data: SessionData = {
      baseUrl: this.baseUrl,
      token: this.token,
      tenantId: this.tenantId,
      userId: this.userId,
      username: this.username,
      md5key: this.md5key,
      publicKey: this.publicKey,
      currentProjectUuid: this.currentProjectUuid,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }

  isAuthenticated(): boolean {
    return this.baseUrl !== null && this.token !== null;
  }

  setCurrentProject(projectUuid: string) {
    this.currentProjectUuid = projectUuid;
    this.saveSession();
  }

  getCurrentProject(): string | null {
    return this.currentProjectUuid;
  }

  /**
   * Get the current project UUID, throwing if none is set.
   * Use this when a projectUuid parameter is required for an API call.
   */
  requireCurrentProject(): string {
    const uuid = this.currentProjectUuid;
    if (!uuid) {
      throw new Error(
        'No current project is set. Call setCurrentProject(uuid), getCurrentProjectFromStorage(), ' +
          'or configureAuto() with projectKey/projectDecrypt first.',
      );
    }
    return uuid;
  }

  /**
   * Read the current project UUID from localStorage and set it as the active project.
   *
   * - key defaults to `"cdp.pu"`
   - If the value is encrypted, provide a `decrypt` function
   *
   * Example with HT for Web decryption:
   * ```typescript
   * session.getCurrentProjectFromStorage({
   *   decrypt: (encrypted) => (window as any).ht.Default.parse(encrypted),
   * });
   * ```
   */
  getCurrentProjectFromStorage(options?: {
    key?: string;
    decrypt?: (encrypted: string) => string;
  }): string | null {
    const key = options?.key ?? 'cdp.pu';
    const encrypted = sessionStorage.getItem(key);
    if (!encrypted) {
      return null;
    }

    const decryptFn =
      options?.decrypt ??
      (typeof (window as any).ht?.Default?.parse === 'function'
        ? (v: string) => (window as any).ht.Default.parse(v)
        : undefined);
    const value = decryptFn ? decryptFn(encrypted) : encrypted;


    this.setCurrentProject(value);
    return value;
  }

  setServer(url: string) {
    this.baseUrl = url.replace(/\/$/, '');
  }

  /** Set access token directly (for token-based API access without login). */
  setToken(token: string) {
    this.token = token;
    this.saveSession();
  }

  /**
   * Quick configure server and token for direct API access.
   * Useful when you already have a valid token from another auth flow.
   */
  configure(baseUrl: string, token: string) {
    this.setServer(baseUrl);
    this.setToken(token);
  }

  /**
   * Auto-configure session from browser environment.
   *
   * - baseUrl defaults to `window.location.origin`
   * - token is read from `localStorage` (default key: `"cdp.at"`)
   * - If the token is encrypted, provide a `decrypt` function
   *
   * Example with HT for Web decryption:
   * ```typescript
   * session.configureAuto({
   *   decrypt: (encrypted) => (window as any).ht.Default.parse(encrypted),
   * });
   * ```
   */
  configureAuto(options?: {
    baseUrl?: string;
    tokenKey?: string;
    decrypt?: (encryptedToken: string) => string;
    projectKey?: string;
    projectDecrypt?: (encryptedProject: string) => string;
  }) {
    const baseUrl =
      options?.baseUrl ??
      (typeof window !== 'undefined' ? window.location.origin : '');
    if (!baseUrl) {
      throw new Error('baseUrl not provided and window.location is unavailable.');
    }

    const tokenKey = options?.tokenKey ?? 'cdp.at';
    const encryptedToken = localStorage.getItem(tokenKey);
    if (!encryptedToken) {
      throw new Error(
        `Token not found in localStorage with key "${tokenKey}".`
      );
    }
    const decryptFn =
      options?.decrypt ??
      (typeof (window as any).ht?.Default?.parse === 'function'
        ? (v: string) => (window as any).ht.Default.parse(v)
        : undefined);
    const token = decryptFn ? decryptFn(encryptedToken) : encryptedToken;

    this.setServer(baseUrl);
    this.setToken(token);

    const encryptedUid = localStorage.getItem('cdp.ud');
    this.userId = encryptedUid ? (decryptFn ? decryptFn(encryptedUid) : encryptedUid) : null;

    const encryptedUName = localStorage.getItem('cdp.nm');
    this.username = encryptedUName ? (decryptFn ? decryptFn(encryptedUName) : encryptedUName) : null;

    const encryptedTd = localStorage.getItem('cdp.td');
    this.tenantId = encryptedTd ? (decryptFn ? decryptFn(encryptedTd) : encryptedTd) : null;

    // Auto-configure current project if key is provided or defaults to 'cdp.pu'
    const projectKey = options?.projectKey ?? 'cdp.pu';
    const encryptedProject = sessionStorage.getItem(projectKey);
    this.currentProjectUuid = null;
    if (encryptedProject) {
      const projectDecryptFn =
        options?.projectDecrypt ??
        (typeof (window as any).ht?.Default?.parse === 'function'
          ? (v: string) => (window as any).ht.Default.parse(v)
          : undefined);
      const projectUuid = projectDecryptFn
        ? projectDecryptFn(encryptedProject)
        : encryptedProject;
      this.setCurrentProject(projectUuid);
    }
    this.saveSession();
  }

  /** Get current auth info including token and server. */
  getAuthInfo(): { baseUrl: string | null; token: string | null; username: string | null; tenantId: number | null } {
    return {
      baseUrl: this.baseUrl,
      token: this.token,
      username: this.username,
      tenantId: this.tenantId,
    };
  }

  /** Diagnostics safe to return to an agent; never include the access token. */
  getSafeAuthInfo() {
    return { baseUrl: this.baseUrl, authenticated: this.isAuthenticated(), projectUuid: this.currentProjectUuid };
  }

  /** Fetch RSA public key from server. */
  async getPublicKey(): Promise<string> {
    if (!this.baseUrl) {
      throw new Error('Server URL not set. Use session.setServer(url) first.');
    }
    const resp = await this.axiosInstance.get(`${this.baseUrl}/api/auth/public-key`, { timeout: 10000 });
    return resp.data as string;
  }

  /** Fetch captcha image and validation ID. */
  async getCaptcha(): Promise<CaptchaInfo> {
    if (!this.baseUrl) {
      throw new Error('Server URL not set. Use session.setServer(url) first.');
    }
    const t = Date.now();
    const resp = await this.axiosInstance.get(`${this.baseUrl}/api/auth/captcha?t=${t}`, { timeout: 10000 });
    return {
      uuid: resp.data.uuid,
      base64: resp.data.base64,
    };
  }

  /** Encrypt password with RSA public key (PKCS#1 v1.5). */
  encryptPassword(publicKey: string, password: string): string {
    if (publicKey.includes(':')) {
      throw new Error('SM2 encryption is not supported. Please use a server with RSA public key.');
    }
    const pem = derToPem(publicKey);
    const crypt = new JSEncrypt();
    crypt.setPublicKey(pem);
    const encrypted = crypt.encrypt(password);
    if (!encrypted) {
      throw new Error('Password encryption failed');
    }
    return encrypted;
  }

  /**
   * Authenticate with the CDP server.
   *
   * @param username Login username.
   * @param password Login password (plaintext, will be encrypted).
   * @param validateCode Optional captcha code. If not provided, caller should first call getCaptcha().
   * @param validateCodeId Captcha UUID (required if validateCode is provided).
   */
  async login(
    username: string,
    password: string,
    validateCode?: string,
    validateCodeId?: string,
  ): Promise<Record<string, any>> {
    if (!this.baseUrl) {
      throw new Error('Server URL not set. Use session.setServer(url) first.');
    }

    // Step 1: Get public key
    const publicKey = await this.getPublicKey();
    this.publicKey = publicKey;

    // Step 2: Validate captcha params
    if (validateCode && !validateCodeId) {
      throw new Error('validateCodeId is required when validateCode is provided');
    }

    // Step 3: Encrypt password
    const encryptedPassword = this.encryptPassword(publicKey, password);

    // Step 4: Get grant type
    let grantType = '';
    try {
      const grantResp = await this.axiosInstance.get(`${this.baseUrl}/api/auth/grant-type`, { timeout: 10000 });
      if (grantResp.status === 200) {
        grantType = String(grantResp.data).trim().replace(/^"|"$/g, '');
      }
    } catch {
      // ignore
    }

    // Step 5: Submit login
    const loginUrl = `${this.baseUrl}/api/login`;
    const response = await this.axiosInstance.post(
      loginUrl,
      new URLSearchParams({
        username,
        password: encryptedPassword,
        validateCode: validateCode || '',
        remember_me: 'false',
        validateCodeId: validateCodeId || '',
        grantType,
      }),
      {
        timeout: 30000,
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      },
    );

    const data = response.data as Record<string, any>;

    // Step 6: Parse response
    if (data.token) {
      this.token = data.token;
      this.username = username;
      this.md5key = data.md5key ?? null;
      const userInfo = data.userInfo;
      if (userInfo) {
        this.userId = userInfo.userId ?? null;
        this.tenantId = userInfo.tenantId ?? null;
      }
    } else if (data.code !== 200 && !data.success) {
      const msg = data.msg || data.message || 'Login failed';
      throw new Error(msg);
    }

    this.saveSession();
    return data;
  }

  logout() {
    this.token = null;
    this.tenantId = null;
    this.userId = null;
    this.username = null;
    this.md5key = null;
    this.currentProjectUuid = null;
    if (this.persistent) localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Make an authenticated API request.
   */
  async request<T = any>(
    method: string,
    endpoint: string,
    config?: RequestConfig,
  ): Promise<T> {
    if (!this.baseUrl) {
      throw new Error('Server URL not set. Use session.setServer(url) first.');
    }

    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = { ...(config?.headers ?? {}) };

    if (this.token && !headers['Authorization']) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    if (['POST', 'PUT', 'PATCH'].includes(method.toUpperCase()) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const resp: AxiosResponse<T> = await this.axiosInstance.request({
      method,
      url,
      headers,
      params: config?.params,
      data: config?.data,
      timeout: config?.timeout ?? 60000,
      responseType: config?.responseType ?? 'json',
    });

    return resp.data;
  }

  async get<T = any>(endpoint: string, config?: Omit<RequestConfig, 'data'>): Promise<T> {
    return this.request<T>('GET', endpoint, config);
  }

  async post<T = any>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('POST', endpoint, config);
  }

  async put<T = any>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>('PUT', endpoint, config);
  }

  async delete<T = any>(endpoint: string, config?: Omit<RequestConfig, 'data'>): Promise<T> {
    return this.request<T>('DELETE', endpoint, config);
  }
}

// Global session instance
let _session: CDPSession | null = null;

/** Get or create the global session instance. */
export function getSession(): CDPSession {
  if (!_session) {
    _session = new CDPSession();
  }
  return _session;
}

/** Reset the global session instance. */
export function resetSession() {
  _session = null;
}
